import { Component, OnInit, OnDestroy, ChangeDetectorRef, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-hero-slider',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-slider.component.html',
  styleUrls: ['./hero-slider.component.scss'],
})
export class HeroSliderComponent implements OnInit, OnDestroy, AfterViewChecked {
  slides: any[] = [];
  currentSlideIndex: number = 0;
  previousSlideIndex: number = 0;
  isTransitioning: boolean = false;
  private autoRotateInterval: any;
  private autoRotateDelay: number = 5000; // 5 seconds
  private langChangeSubscription: Subscription;
  private lastSlideIndex: number = -1;

  constructor(public translate: TranslateService, private cdr: ChangeDetectorRef) {
    // Subscribe to language changes to update slides
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      // Force change detection
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.slides = [
      {
        src: 'assets/images/banner/4.png',
        logo: 'assets/images/logo/cal-logo-white-2x.png',
        titleEn: `Your Journey to Financial<br>Freedom Starts Today`,
        titleBn: `আর্থিক স্বাধীনতার<br>যাত্রা আজই শুরু করুন`,
        subtitleEn: `Simple onboarding, seamless investing, powerful tools.`,
        subtitleBn: `সহজ অনবোর্ডিং, নিরবচ্ছিন্ন বিনিয়োগ, শক্তিশালী সরঞ্জাম।`,
        buttonEn: 'Start Now',
        buttonBn: 'এখনই শুরু করুন',
        link: '/signin',
        external: false
      },
      {
        src: 'assets/images/banner/5.png',
        logo: 'assets/images/logo/cal-logo-white-2x.png',
        titleEn: `New to trading? Practice with zero risk on VSTOCK`,
        titleBn: `ট্রেডিংয়ে নতুন? VSTOCK-এ শূন্য ঝুঁকি নিয়ে অনুশীলন করুন`,
        subtitleEn: `CAL's virtual stock trading simulator. Learn the market. Place virtual trades. Build confidence.`,
        subtitleBn: `CAL-এর ভার্চুয়াল স্টক ট্রেডিং সিমুলেটর। বাজার শিখুন। ভার্চুয়াল ট্রেড করুন। আত্মবিশ্বাস গড়ে তুলুন।`,
        buttonEn: 'Try Now',
        buttonBn: 'এখনই চেষ্টা করুন',
        link: 'https://calbd.com.bd/vstock/',
        external: true
      },
      {
        src: 'assets/images/banner/2.png',
        logo: 'assets/images/logo/cal-logo-white-2x.png',
        titleEn: `Invest in Bangladesh's<br>Leading Companies`,
        titleBn: `বাংলাদেশের<br>অগ্রগামী কোম্পানিতে বিনিয়োগ করুন`,
        subtitleEn: `Build wealth with secure, modern investment tools by CAL Bangladesh.`,
        subtitleBn: `CAL বাংলাদেশের নিরাপদ, আধুনিক বিনিয়োগ সরঞ্জাম দিয়ে সম্পদ গড়ে তুলুন।`,
        buttonEn: 'Learn More',
        buttonBn: 'আরও জানুন',
        link: '/signin',
        external: false
      },
      {
        src: 'assets/images/banner/3.png',
        logo: 'assets/images/logo/cal-logo-white-2x.png',
        titleEn: `Build Your Portfolio with<br>Confidence and Expertise`,
        titleBn: `আত্মবিশ্বাস এবং দক্ষতার সাথে<br>আপনার পোর্টফোলিও তৈরি করুন`,
        subtitleEn: `Professional investment services designed to help you achieve long-term financial success.`,
        subtitleBn: `দীর্ঘমেয়াদী আর্থিক সাফল্য অর্জনে সহায়তা করার জন্য ডিজাইন করা পেশাদার বিনিয়োগ পরিষেবা।`,
        buttonEn: 'Explore Now',
        buttonBn: 'এখনই অন্বেষণ করুন',
        link: '/signin',
        external: false
      }
    ];

    // Auto-rotate slides every 5 seconds
    this.startAutoRotate();
  }

  ngOnDestroy(): void {
    this.stopAutoRotate();
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  nextSlide(): void {
    if (this.isTransitioning) return;
    this.previousSlideIndex = this.currentSlideIndex;
    this.currentSlideIndex = (this.currentSlideIndex + 1) % this.slides.length;
    this.startTransition();
  }

  prevSlide(): void {
    if (this.isTransitioning) return;
    this.previousSlideIndex = this.currentSlideIndex;
    this.currentSlideIndex = (this.currentSlideIndex - 1 + this.slides.length) % this.slides.length;
    this.startTransition();
  }

  goToSlide(index: number): void {
    if (this.isTransitioning || index === this.currentSlideIndex) return;
    this.previousSlideIndex = this.currentSlideIndex;
    this.currentSlideIndex = index;
    this.startTransition();
    // Restart auto-rotate after manual navigation - DISABLED
    // this.restartAutoRotate();
  }

  private startTransition(): void {
    this.isTransitioning = true;
    // Trigger content animation
    setTimeout(() => {
      this.triggerContentAnimation();
    }, 50);
    setTimeout(() => {
      this.isTransitioning = false;
    }, 800); // Match transition duration
  }

  ngAfterViewChecked(): void {
    if (this.lastSlideIndex !== this.currentSlideIndex) {
      this.lastSlideIndex = this.currentSlideIndex;
      setTimeout(() => {
        this.triggerContentAnimation();
      }, 50);
    }
  }

  private triggerContentAnimation(): void {
    // This will be handled by CSS animations triggered by data-slide attribute change
    this.cdr.detectChanges();
  }

  get currentSlide(): any {
    const slide = this.slides[this.currentSlideIndex];
    if (slide) {
      const isBengali = this.translate.currentLang === 'bn' || this.translate.currentLang === 'bn-BD';
      return {
        ...slide,
        title: isBengali ? slide.titleBn : slide.titleEn,
        subtitle: isBengali ? slide.subtitleBn : slide.subtitleEn,
        button: isBengali ? slide.buttonBn : slide.buttonEn
      };
    }
    return slide;
  }

  private startAutoRotate(): void {
    this.stopAutoRotate();
    this.autoRotateInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoRotateDelay);
  }

  private stopAutoRotate(): void {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
  }

  private restartAutoRotate(): void {
    this.stopAutoRotate();
    this.startAutoRotate();
  }
}
